import { supabase } from '@/integrations/supabase/client';
import type { Tables, TablesInsert } from '@/integrations/supabase/types';

// Type aliases for easier use
type DatabaseQuestionCatalog = Tables<'question_catalogs'>;
type DatabaseQuestion = Tables<'questions'>;
type DatabaseQuestionAnswer = Tables<'question_answers'>;
type QuestionCatalogInsert = TablesInsert<'question_catalogs'>;
type QuestionInsert = TablesInsert<'questions'>;
type QuestionAnswerInsert = TablesInsert<'question_answers'>;

// App types for question catalogs
export interface QuestionCatalog {
  id: string;
  name: string;
  description?: string;
  totalQuestions: number;
  stateSpecificQuestions: number;
  imageUrl?: string;
  userId?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface QuestionAnswer {
  id: string;
  answerIndex: number;
  germanAnswer: string;
  englishAnswer?: string | null;
  vietnameseAnswer?: string | null;
  isCorrect: boolean;
}

export interface Question {
  id: string;
  catalogId: string;
  questionNumber: number;
  germanQuestion: string;
  englishQuestion?: string | null;
  vietnameseQuestion?: string | null;
  imageUrl?: string | null;
  stateSpecificFor?: string | null;
  answers: QuestionAnswer[];
  createdAt: Date;
  updatedAt: Date;
}

export interface QuestionCatalogWithQuestions extends QuestionCatalog {
  questions: Question[];
}

// Convert database types to app types
const convertDatabaseCatalogToAppCatalog = (
  dbCatalog: DatabaseQuestionCatalog
): QuestionCatalog => ({
  id: dbCatalog.id,
  name: dbCatalog.name,
  description: dbCatalog.description || undefined,
  totalQuestions: dbCatalog.total_questions,
  stateSpecificQuestions: dbCatalog.state_specific_questions,
  imageUrl: dbCatalog.image_url || undefined,
  userId: dbCatalog.user_id || undefined,
  createdAt: new Date(dbCatalog.created_at!),
  updatedAt: new Date(dbCatalog.updated_at!),
});

const convertDatabaseQuestionToAppQuestion = (
  dbQuestion: DatabaseQuestion,
  dbAnswers: DatabaseQuestionAnswer[]
): Question => ({
  id: dbQuestion.id,
  catalogId: dbQuestion.catalog_id!,
  questionNumber: dbQuestion.question_number,
  germanQuestion: dbQuestion.german_question,
  englishQuestion: dbQuestion.english_question,
  vietnameseQuestion: dbQuestion.vietnamese_question,
  imageUrl: dbQuestion.image_url,
  stateSpecificFor: dbQuestion.state_specific_for,
  answers: dbAnswers
    .sort((a, b) => a.answer_index - b.answer_index)
    .map(answer => ({
      id: answer.id,
      answerIndex: answer.answer_index,
      germanAnswer: answer.german_answer,
      englishAnswer: answer.english_answer,
      vietnameseAnswer: answer.vietnamese_answer,
      isCorrect: answer.is_correct,
    })),
  createdAt: new Date(dbQuestion.created_at!),
  updatedAt: new Date(dbQuestion.updated_at!),
});

// Fetch all question catalogs
export const fetchQuestionCatalogs = async (): Promise<QuestionCatalog[]> => {
  try {
    const { data: catalogs, error } = await supabase
      .from('question_catalogs')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching question catalogs:', error);
      return [];
    }

    return (catalogs || []).map(convertDatabaseCatalogToAppCatalog);
  } catch (error) {
    console.error('Unexpected error fetching question catalogs:', error);
    return [];
  }
};

// Fetch a single question catalog with all its questions and answers
export const fetchQuestionCatalogWithQuestions = async (
  catalogId: string
): Promise<QuestionCatalogWithQuestions | null> => {
  try {
    // Fetch catalog
    const { data: catalog, error: catalogError } = await supabase
      .from('question_catalogs')
      .select('*')
      .eq('id', catalogId)
      .single();

    if (catalogError || !catalog) {
      console.error('Error fetching question catalog:', catalogError);
      return null;
    }

    // Fetch questions for this catalog
    const { data: questions, error: questionsError } = await supabase
      .from('questions')
      .select('*')
      .eq('catalog_id', catalogId)
      .order('question_number', { ascending: true });

    if (questionsError) {
      console.error('Error fetching questions:', questionsError);
      return {
        ...convertDatabaseCatalogToAppCatalog(catalog),
        questions: [],
      };
    }

    // Fetch answers for all questions
    let questionAnswers: DatabaseQuestionAnswer[] = [];
    if (questions && questions.length > 0) {
      const questionIds = questions.map(q => q.id);
      const { data: answers, error: answersError } = await supabase
        .from('question_answers')
        .select('*')
        .in('question_id', questionIds)
        .order('answer_index', { ascending: true });

      if (answersError) {
        console.error('Error fetching question answers:', answersError);
      } else {
        questionAnswers = answers || [];
      }
    }

    // Group answers by question_id
    const answersByQuestionId = questionAnswers.reduce(
      (acc, answer) => {
        if (!acc[answer.question_id!]) {
          acc[answer.question_id!] = [];
        }
        acc[answer.question_id!].push(answer);
        return acc;
      },
      {} as Record<string, DatabaseQuestionAnswer[]>
    );

    // Convert questions to app format
    const appQuestions = (questions || []).map(question =>
      convertDatabaseQuestionToAppQuestion(
        question,
        answersByQuestionId[question.id] || []
      )
    );

    return {
      ...convertDatabaseCatalogToAppCatalog(catalog),
      questions: appQuestions,
    };
  } catch (error) {
    console.error(
      'Unexpected error fetching question catalog with questions:',
      error
    );
    return null;
  }
};

// Get questions for practice mode (optionally filtered by state)
export const fetchQuestionsForPractice = async (
  catalogId: string,
  stateSpecificFor?: string | null,
  randomize: boolean = false
): Promise<Question[]> => {
  try {
    let query = supabase
      .from('questions')
      .select(
        `
        *,
        question_answers (*)
      `
      )
      .eq('catalog_id', catalogId);

    // Filter by state if specified
    if (stateSpecificFor) {
      query = query.or(
        `state_specific_for.is.null,state_specific_for.eq.${stateSpecificFor}`
      );
    }

    // Order by question number unless randomizing
    if (!randomize) {
      query = query.order('question_number', { ascending: true });
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching questions for practice:', error);
      return [];
    }

    if (!data) {
      return [];
    }

    let questions = data.map(item => {
      const question = item as DatabaseQuestion & {
        question_answers: DatabaseQuestionAnswer[];
      };
      return convertDatabaseQuestionToAppQuestion(
        question,
        question.question_answers
      );
    });

    // Randomize if requested
    if (randomize) {
      questions = questions.sort(() => Math.random() - 0.5);
    }

    return questions;
  } catch (error) {
    console.error('Unexpected error fetching questions for practice:', error);
    return [];
  }
};

// Save a new question catalog with questions and answers
export const saveQuestionCatalog = async (
  userId: string,
  catalog: Omit<QuestionCatalogWithQuestions, 'id' | 'createdAt' | 'updatedAt'>
): Promise<QuestionCatalogWithQuestions> => {
  try {
    if (!userId) {
      throw new Error('User ID is required to save question catalogs');
    }

    // Insert catalog
    const catalogInsert: QuestionCatalogInsert = {
      name: catalog.name,
      description: catalog.description || null,
      image_url: catalog.imageUrl || null,
      user_id: userId,
      total_questions: catalog.questions.length,
      state_specific_questions: catalog.questions.filter(
        q => q.stateSpecificFor
      ).length,
    };

    const { data: savedCatalog, error: catalogError } = await supabase
      .from('question_catalogs')
      .insert(catalogInsert)
      .select()
      .single();

    if (catalogError || !savedCatalog) {
      console.error('Error saving question catalog:', catalogError);
      throw new Error('Failed to save question catalog');
    }

    // Insert questions
    const questionInserts: QuestionInsert[] = catalog.questions.map(
      question => ({
        catalog_id: savedCatalog.id,
        question_number: question.questionNumber,
        german_question: question.germanQuestion,
        english_question: question.englishQuestion || null,
        vietnamese_question: question.vietnameseQuestion || null,
        image_url: question.imageUrl || null,
        state_specific_for: question.stateSpecificFor || null,
      })
    );

    const { data: savedQuestions, error: questionsError } = await supabase
      .from('questions')
      .insert(questionInserts)
      .select();

    if (questionsError || !savedQuestions) {
      console.error('Error saving questions:', questionsError);
      // Try to delete the catalog since questions failed
      await supabase
        .from('question_catalogs')
        .delete()
        .eq('id', savedCatalog.id);
      throw new Error('Failed to save questions');
    }

    // Insert question answers
    const allAnswerInserts: QuestionAnswerInsert[] = [];
    for (let i = 0; i < catalog.questions.length; i++) {
      const question = catalog.questions[i];
      const savedQuestion = savedQuestions[i];

      const answerInserts = question.answers.map(answer => ({
        question_id: savedQuestion.id,
        answer_index: answer.answerIndex,
        german_answer: answer.germanAnswer,
        english_answer: answer.englishAnswer || null,
        vietnamese_answer: answer.vietnameseAnswer || null,
        is_correct: answer.isCorrect,
      }));

      allAnswerInserts.push(...answerInserts);
    }

    const { data: savedAnswers, error: answersError } = await supabase
      .from('question_answers')
      .insert(allAnswerInserts)
      .select();

    if (answersError) {
      console.error('Error saving question answers:', answersError);
      // Delete the catalog and questions since answers failed
      await supabase
        .from('question_catalogs')
        .delete()
        .eq('id', savedCatalog.id);
      throw new Error('Failed to save question answers');
    }

    // Group saved answers by question_id
    const answersByQuestionId = (savedAnswers || []).reduce(
      (acc, answer) => {
        if (!acc[answer.question_id!]) {
          acc[answer.question_id!] = [];
        }
        acc[answer.question_id!].push(answer);
        return acc;
      },
      {} as Record<string, DatabaseQuestionAnswer[]>
    );

    // Convert to app format and return
    const appQuestions = savedQuestions.map(question =>
      convertDatabaseQuestionToAppQuestion(
        question,
        answersByQuestionId[question.id] || []
      )
    );

    return {
      ...convertDatabaseCatalogToAppCatalog(savedCatalog),
      questions: appQuestions,
    };
  } catch (error) {
    console.error('Error in saveQuestionCatalog:', error);
    throw error;
  }
};

// Update question translations (for individual questions after initial creation)
export const updateQuestionTranslations = async (
  questionId: string,
  englishQuestion?: string,
  vietnameseQuestion?: string
): Promise<void> => {
  try {
    const { error } = await supabase
      .from('questions')
      .update({
        english_question: englishQuestion || null,
        vietnamese_question: vietnameseQuestion || null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', questionId);

    if (error) {
      console.error('Error updating question translations:', error);
      throw new Error('Failed to update question translations');
    }
  } catch (error) {
    console.error('Error in updateQuestionTranslations:', error);
    throw error;
  }
};

// Update answer translations
export const updateAnswerTranslations = async (
  answerId: string,
  englishAnswer?: string,
  vietnameseAnswer?: string
): Promise<void> => {
  try {
    const { error } = await supabase
      .from('question_answers')
      .update({
        english_answer: englishAnswer || null,
        vietnamese_answer: vietnameseAnswer || null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', answerId);

    if (error) {
      console.error('Error updating answer translations:', error);
      throw new Error('Failed to update answer translations');
    }
  } catch (error) {
    console.error('Error in updateAnswerTranslations:', error);
    throw error;
  }
};

// Add image to question
export const updateQuestionImage = async (
  questionId: string,
  imageUrl: string
): Promise<void> => {
  try {
    const { error } = await supabase
      .from('questions')
      .update({
        image_url: imageUrl,
        updated_at: new Date().toISOString(),
      })
      .eq('id', questionId);

    if (error) {
      console.error('Error updating question image:', error);
      throw new Error('Failed to update question image');
    }
  } catch (error) {
    console.error('Error in updateQuestionImage:', error);
    throw error;
  }
};

// Update a question's image URL in the catalog (for unsaved catalogs)
export const updateQuestionImageInCatalog = (
  catalog: QuestionCatalogWithQuestions,
  questionIndex: number,
  imageUrl: string | null
): QuestionCatalogWithQuestions => {
  const updatedCatalog = { ...catalog };
  updatedCatalog.questions = [...catalog.questions];
  updatedCatalog.questions[questionIndex] = {
    ...catalog.questions[questionIndex],
    imageUrl,
    updatedAt: new Date(),
  };
  return updatedCatalog;
};

// Delete a question catalog (and its questions/answers due to CASCADE)
export const deleteQuestionCatalog = async (
  catalogId: string
): Promise<void> => {
  try {
    const { error } = await supabase
      .from('question_catalogs')
      .delete()
      .eq('id', catalogId);

    if (error) {
      console.error('Error deleting question catalog:', error);
      throw new Error('Failed to delete question catalog');
    }
  } catch (error) {
    console.error('Error in deleteQuestionCatalog:', error);
    throw error;
  }
};
